import React, { useState } from "react";
import {
  Card,
  Grid,
  Typography,
  Box,
  TextField,
} from "@mui/material";
import { IconButton } from "@mui/material";

import { useRef } from "react";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useDispatch } from "react-redux";
import { toggleFavorite } from "../../redux/reducers/favoritesSlice";
import { useSelector } from "react-redux";
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ProjectDetails from "./ProjectDetails";
import { useTheme, useMediaQuery } from "@mui/material";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoginModal from "../common/LoginModal";


const ProjectPlotStructure = ({ data, type, isAuthenticated }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);

  const plots = data.plots || [];
  const [selectedPlot, setSelectedPlot] = useState(() => {
    return plots.find(
      (plot) => {
        const status = plot.Status?.toLowerCase();
        return status !== "sold" && status !== "sale initiated";
      }
    ) || null;
  });

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const dispatch = useDispatch();
  const themes = useSelector((data) => data.themeValue.colors);
  const themeMode = useSelector((data) => data.themeValue.theme);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const modalRef = useRef();
  const userId = useSelector((state) => state.user.userId);
  // const [zoomLevel, setZoomLevel] = useState(1);
  const [transformOrigin, setTransformOrigin] = useState("center center");
  const containerRef = useRef();

  const handleDoubleClick = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const originX = `${(offsetX / rect.width) * 100}%`;
    const originY = `${(offsetY / rect.height) * 100}%`;

    setTransformOrigin(`${originX} ${originY}`);

    // Toggle zoom: If zoomed in, zoom out. Else zoom in more.
    // setZoomLevel((prev) => (prev > 1 ? 1 : 2));
  };
  const columns = 2;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));  // <600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–899px
  const isLaptop = useMediaQuery(theme.breakpoints.up("md")); // ≥900px
  let plotsPerBlock = 6;
  let columnsPerRow = 3; // Default to 3 columns for desktop
  let rowsPerBlock = 2;

  if (isTablet) {
    plotsPerBlock = 6;
    columnsPerRow = 2; // 2 columns for tablet
    rowsPerBlock = 3;
  } else if (isMobile) {
    plotsPerBlock = 2;
    columnsPerRow = 2; // 1 column for mobile
    rowsPerBlock = 2;

  }


  const numberOfRows = Math.ceil(data.number_of_plots / columns);
  const plotsMap = new Map(
    plots.map((p) => {
      const match = p.plotName.match(/\d+/);
      const plotNumber = match ? parseInt(match[0], 10) : null;
      return [plotNumber, p];
    })
  );
  const handlePlotSelect = (plot) => {
    if (plot.Status?.toLowerCase() !== "sold" && plot.Status?.toLowerCase() !== "sale initiated") {
      setSelectedPlot(plot);
    }
  };
  const renderGrid = () => {
    const availablePlots = [];
    const bookedPlots = [];

    plots.forEach((plot) => {
      const isSold = plot.Status?.toLowerCase() === "sold";
      const isInitiated = plot.Status?.toLowerCase() === "sale initiated";
      const matchesSearch = plot.plotName.toLowerCase().includes(searchTerm.toLowerCase());

      // ✅ Show only matching plots if searchTerm exists
      if (searchTerm && !matchesSearch) return;

      if (!isSold && !isInitiated) {
        availablePlots.push(plot);
      } else {
        bookedPlots.push(plot);
      }
    });

    // Combine arrays: available first, then booked/sold
    const sortedPlots = [...availablePlots, ...bookedPlots];

    const blocks = [];
    for (let i = 0; i < sortedPlots.length; i += plotsPerBlock) {
      const blockPlots = sortedPlots.slice(i, i + plotsPerBlock);

      blocks.push(
        <Grid
          key={`block-${i}`}
          container
          spacing={2}
          direction="column"
          sx={{ minWidth: "auto", marginRight: 1, marginTop: 2 }}
        >
          {[...Array(rowsPerBlock)].map((_, rowIdx) => (
            <Grid container item spacing={2} key={`row-${i}-${rowIdx}`}>
              {[...Array(columnsPerRow)].map((_, colIdx) => {
                const plotIndex = rowIdx * columnsPerRow + colIdx;
                const plot = blockPlots[plotIndex];

                if (!plot)
                  return <Grid xs key={`empty-${i}-${rowIdx}-${colIdx}`} />;

                const isSold = plot.Status?.toLowerCase() === "sold";
                const isInitiated = plot.Status?.toLowerCase() === "sale initiated";

                return (
                  <Grid xs={12 / columnsPerRow} key={plot.plotName}>
                    <Card
                      sx={{
                        width: {
                          xs: columnsPerRow === 1 ? "100%" : 120,
                          sm: columnsPerRow === 2 ? 120 : 100,
                          md: 100,
                        },
                        height: {
                          xs: columnsPerRow === 1 ? 120 : 100,
                          sm: 100,
                          md: 100,
                        },
                        border: 2,
                        borderColor: selectedPlot?.plotName === plot.plotName && plot.SizeInSqFt
                          ? "primary.main"
                          : isSold
                            ? "error.dark"
                            : isInitiated
                              ? "orange"
                              : "grey.800",
                        backgroundColor: isSold
                          ? "#FF4D4D"
                          : isInitiated
                            ? "orange"
                            : selectedPlot?.plotName === plot.plotName && plot.SizeInSqFt
                              ? "#B3E5FC"
                              : "#D0F0C0",
                        borderRadius: 2,
                        padding: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor:
                          isSold || isInitiated ? "not-allowed" : "pointer",
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform:
                            isSold || isInitiated ? "none" : "scale(1.05)",
                          boxShadow: isSold || isInitiated ? "none" : 3,
                        },
                      }}
                      onClick={() => handlePlotSelect(plot)}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography fontWeight="bold" fontSize={14} align="center">
                          {plot.plotName}
                        </Typography>
                      </Box>
                      {plot?.SizeInSqFt && (
                        <Typography fontSize={10} align="center">
                          Size: {plot.SizeInSqFt} sqft
                        </Typography>
                      )}

                      {plot?.latestMarketSqFtPrice && (
                        <Typography fontSize={10} align="center">
                          ₹{plot.latestMarketSqFtPrice}/sqft
                        </Typography>
                      )}


                      {isSold && (
                        <Typography
                          fontWeight="bold"
                          color="white"
                          mt={1}
                          fontSize={10}
                        >
                          SOLD
                        </Typography>
                      )}
                      {isInitiated && (
                        <Typography
                          fontWeight="bold"
                          color="white"
                          mt={1}
                          fontSize={10}
                        >
                          Booked Plot
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </Grid>
      );
    }

    return blocks.length ? blocks : (
      <Typography align="center" sx={{ mt: 4, color: "text.secondary" }}>
        No plots found for “{searchTerm}”
      </Typography>
    );
  };





  const encryptionKey = 'yourSecretKey';

  const scrollRef = useRef(null);
  const getUniqueId = (property) => {
    const typeMap = {
      1: 'project',
      2: 'Property',
      3: 'Apartment',
      4: 'IndividualVilla'
    };

    const typeLabel = typeMap[property.type] || 'Unknown';

    return `${typeLabel}_${property.id}`;
  };

  const favoriteItems = useSelector((state) => state.favorites.items);
  const toggleLike = (property) => {
    const uniqueId = getUniqueId(property);
    dispatch(toggleFavorite(uniqueId));
  };


  const isLiked = (property) => {
    const uniqueId = getUniqueId(property);
    return favoriteItems.includes(uniqueId);
  };


  return (
    <>
      <Box
        sx={{
          padding: { xs: 0.5, sm: 3 },
          width: "100%",

          margin: "0px",
          // backgroundColor: "red",
          borderRadius: 2,
          display: "flex",
          flexDirection: {
            xs: "column",   // <600px
            sm: "column",   // 600px–900px
            md: "row",      // ≥900px or ≥1000px
          },
          gap: 2,
          backgroundColor: themes.background
        }}
      >
        {/* Left side - Plots */}
        <Box
          sx={{
            position: "relative", // for positioning arrows
            width: {
              xs: "100%",   // <600px
              sm: "100%",   // 600px–900px
              md: "50%",      // ≥900px or ≥1000px
            },
            height: 450,
            maxHeight: 450,
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: themes.background
          }}
        >
          <Box
            sx={{
              position: "relative", // for positioning arrows
              width: { xs: "100%", sm: "80%" },
              textAlign: "center",
              alignItems: "center"
            }}
          >
            {/* Scrollable area */}
            <Box
              ref={scrollRef}
              sx={{
                width: "100%",
                height: {
                  xs: '100vh',
                  sm: '80vh',
                  md: '62vh',
                },
                textAlign: "center",
                alignItems: "center",
                borderRadius: 2,
                // padding: 2,
                maxHeight: 455,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "hidden",
                scrollBehavior: "smooth",
                backgroundColor: themes.screen_background,
                border: `1px solid `,
                borderColor: themes.text,
                // backgroundColor:"red"
              }}
            >
              {/* Input Field Above Plots */}
              <Box sx={{ backgroundColor: themes.background, width: "100%", position: 'sticky', top: 0, zIndex: 1 }}>
                {!showSearch ? (
                  //  Show Total Plots + Search Icon
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      // backgroundColor: theme.background,
                      padding: "5px",
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormatListBulletedIcon sx={{ color: themes.normal_icon }} />
                      <Typography fontWeight="bold" sx={{ color: themes.text }}>
                        Plots
                      </Typography>
                    </Box>
                    <IconButton onClick={() => setShowSearch(true)}>
                      <SearchIcon sx={{ color: themes.text }} />
                    </IconButton>
                  </Box>
                ) : (
                  //  Show Search Input with Back Icon
                  <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "5px",
                    borderRadius: 1
                  }}>
                    {/* <IconButton onClick={() => setShowSearch(false)}>
            <ArrowBackIcon />
          </IconButton> */}
                    <IconButton onClick={() => setShowSearch(false)}>
                      <SearchIcon sx={{ color: theme.normal_icon }} />
                    </IconButton>
                    <TextField
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "12px",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "12px",
                        },
                        input: {
                          color: theme.text,
                        },
                        label: {
                          color: theme.text,
                        },
                        '& label.Mui-focused': {
                          color: theme.primary_color,
                        },
                        border: "none"
                      }}
                      size="small"
                      variant="outlined"
                      placeholder="Search Plot NO..."
                      onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                    />
                  </Box>
                )}
              </Box>
              {/*  Plot Cards */}
              {renderGrid()}
            </Box>




          </Box>
          {/* Share and Heart Icon */}
          <Box sx={{ marginLeft: "10px", flexDirection: "column", justifyContent: "flex-end" }}>
            <Box>
              {/* Share Icon */}
              <IconButton
                sx={{
                  backgroundColor: "#F5F5F5",
                  boxShadow: 2,
                  borderRadius: 2,

                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAuthenticated) {
                    setShowLoginModal(true);
                    return;
                  }
                  setSelectedProperty(data);
                  setShowPopup(true);
                }}
                title="Share Project"
              >
                <ShareOutlinedIcon style={{ fontSize: 18, color: theme.normal_icon }} />
              </IconButton>

              <br />

              {/* Like Icon */}
              <IconButton
                sx={{
                  backgroundColor: "#F5F5F5",
                  boxShadow: 2,
                  borderRadius: 2,
                  marginTop: 2,

                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAuthenticated) {
                    setShowLoginModal(true);
                    return;
                  }
                  toggleLike(data);
                }}
                title="Like"
              >
                {isLiked(data) ? (
                  <FavoriteIcon style={{ fontSize: 20, color: "red" }} />
                ) : (
                  <FavoriteBorderIcon style={{ fontSize: 20, color: theme.normal_icon }} />
                )}
              </IconButton>
            </Box>

            <Box sx={{
              position: "absolute",
              bottom: "5%",
            }}>
              <IconButton
                sx={{
                  backgroundColor: "#F5F5F5",
                  boxShadow: 2,
                  borderRadius: 2,

                }}
                onClick={() =>
                  scrollRef.current.scrollBy({
                    top: -scrollRef.current.clientHeight,
                    behavior: "smooth",
                  })
                }
              >
                <KeyboardArrowUpIcon sx={{ color: theme.normal_icon }} />
              </IconButton>

              <IconButton
                sx={{
                  backgroundColor: "#F5F5F5",
                  boxShadow: 2,
                  borderRadius: 2,
                  mt: 1,

                }}
                onClick={() =>
                  scrollRef.current.scrollBy({
                    top: scrollRef.current.clientHeight,
                    behavior: "smooth",
                  })
                }
              >
                <KeyboardArrowDownIcon />
              </IconButton>


            </Box>
          </Box>
        </Box>
        {/* Right side - Selected Plot Details */}
        <ProjectDetails data={data} selectedPlot={selectedPlot}
          showPopup={showPopup}
          setShowPopup={setShowPopup}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
        />

        {showLoginModal && (
          <LoginModal onClose={() => setShowLoginModal(false)} />
        )}

      </Box></>

  );
};
export default ProjectPlotStructure;